import { useEffect } from "react";
import LandingPageLayout from "../../layout/LandingPageLayout";
import PageMeta from "../../components/common/common/PageMeta";

const AboutPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <LandingPageLayout>
      <PageMeta
        title="About | Casekeep"
        description="Casekeep is a small, bootstrapped business based in Hood River, Oregon. We build secure file sharing software for professionals who handle sensitive client information."
      />
      <section className="py-16 sm:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-8">
            Who's behind this?
          </h1>

          <div className="prose prose-lg dark:prose-invert max-w-none">
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              Casekeep is a small, bootstrapped business based out of Hood
              River, Oregon, USA.{" "}
              <span className="font-bold dark:text-white">
                We build software that makes it easy
              </span>{" "}
              for professionals to securely collect and share sensitive files
              with their clients. That's it.
            </p>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              We're not trying to dominate the market, chase enterprise
              contracts, or disrupt anything.{" "}
              <span className="font-bold dark:text-white">
                We're focused on serving small businesses and independent
                professionals
              </span>{" "}
              — CPAs, attorneys, consultants, non-profits, and bookkeepers who
              handle sensitive client information every day but don't have
              enterprise budgets or dedicated IT teams.
            </p>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              The idea for Casekeep was born during the process of adopting two
              girls from foster care.{" "}
              <span className="font-bold dark:text-white">
                I experienced firsthand how broken secure document sharing
                really is
              </span>
              . The caseworkers I was working with needed to collect and share
              financial information, medical records, background check forms,
              and all sorts of sensitive paperwork — and the options were either
              insecure email attachments or sending micro USBs in the mail 😅.
            </p>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              As a software engineer, I was concerned about the risks in secure
              document sharing. So I built Casekeep to solve it:{" "}
              <span className="font-bold dark:text-white">
                simple, bank-level protection for sharing and collecting client
                information
              </span>
              . What started as a solution for my family's needs turned out to
              be useful for a lot of other folks too — tax preparers, attorneys,
              financial advisors, and anyone else who needs to exchange
              sensitive information without the friction.
            </p>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              We're structured to be lean, profitable, and sustainable. We're
              not beholden to investors, and we're here for the long haul. Our
              goal is simple:{" "}
              <span className="font-bold dark:text-white">
                help make the internet a little more secure, one small business
                at a time
              </span>
              .
            </p>

            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-8">
              If you have any questions, feedback, or if there's anything we can
              do to help, please{" "}
              <a
                href="mailto:hello@casekeep.app"
                className="text-brand-600 dark:text-brand-400 hover:underline"
              >
                get in touch
              </a>
              .
            </p>

            <div className="flex items-center gap-4 mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
              <img
                src="/corinne-casekeep.jpg"
                alt="Corinne, founder of Casekeep"
                className="w-30 h-30 rounded-full object-cover"
              />
              <div>
                <p className="text-gray-900 dark:text-white font-medium">
                  — Corinne
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Founder
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </LandingPageLayout>
  );
};

export default AboutPage;
